import React, { useContext, useEffect, useMemo, useState } from "react";
import "./SalesPayments.css";
import axios from "axios";
import AuthContext from "../../../../contexts/AuthContext";
import TanStackTable from "../../../../components/Table/TanStackTable/TanStackTable";
import { createColumnHelper } from "@tanstack/react-table";
import ContentCreator from "../../../../components/ContentCreator/ContentCreator";
import showNotification from "../../../../utils/showNotification";
import { ERROR_HANDLER } from "../../../../utils/error-handler";
import AppContext from "../../../../contexts/AppContext";
import { SalesPaymentsView } from "../../../../components/SalesPaymentsView/SalesPaymentsView";
import SalesPaymentsForm from "../SalesPaymentsForm/SalesPaymentsForm";
import useS3 from "../../../../hooks/useS3";
import moment from "moment";
import { IconSquareX, IconSquareCheck } from "@tabler/icons-react";
import StudentModal from "../../../../components/StudentModal/StudentModal";
import { Badge, Loader, Modal } from "@mantine/core";
import { currencyFormat } from "../../../../utils/currencyFormat";
import filterFunctions from "../../../../utils/filterFunctions";
import NewSalesPaymentsForm from "../NewSalesPaymentsForm/NewSalesPaymentsForm";
import { DatePickerInput } from "@mantine/dates";
import { useLocation } from "react-router-dom";
import { InstallmentsModal } from "../../../../components/InstallmentsModal/InstallmentsModal";
import { useNavigate } from "react-router-dom";
import { isBase64 } from "../../../../utils/isBase64";

const FORM_CONFIG = {
    CREATE: "CREATE",
    EDIT: "EDIT",
    NONE: "NONE",
};

const createSale = async (values, config, _uploadToS3) => {
    if (values.receipt) {
        const clearTitle = `${values.sellerId}`.replace(/[^\w\s]/g, "").trim();
        values.receipt = await _uploadToS3({
            image: values.receipt,
            fileName: `${clearTitle}-${moment().format("DD-MM-YYYY")}`,
            fileDirectory: "receipts",
            isRequired: true,
        });
    }

    const jsonData = {
        data: {
            ...values,
        },
    };

    return axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/createSale`,
        jsonData,
        config
    );
};

const editSale = async (values, config, _uploadToS3) => {
    values.installments.total_cycles = parseInt(
        values.installments.total_cycles,
        10
    );

    if (values.installments.data) {
        values.installments.data = await Promise.all(
            values.installments.data.map(async (installment, index) => {
                installment.currency = 'USD';

                if (installment.cycle_receipt && isBase64(installment.cycle_receipt)) {
                    const clearTitle = `${values.rowData.payer.email}-cycle-${installment.index + 1}`
                        .replace(/[^\w\s]/g, "")
                        .trim();

                    installment.cycle_receipt = await _uploadToS3({
                        image: installment.cycle_receipt,
                        fileName: `${clearTitle}-${moment().format("DD-MM-YYYY")}`,
                        fileDirectory: "receipts",
                        isRequired: true,
                    });
                }

                return installment;
            })
        );
    }

    if (values.receipt) {
        const clearTitle = `${values.sellerId}`.replace(/[^\w\s]/g, "").trim();
        values.receipt = await _uploadToS3({
            image: values.receipt,
            fileName: `${clearTitle}-${moment().format("DD-MM-YYYY")}`,
            fileDirectory: "receipts",
            isRequired: true,
        });
    }



    const jsonData = {
        data: {
            ...values,
            isNewCustomer: values.isNewCustomer === "true" ? true : false,
            is_already_in_wp: values.is_already_in_wp
        },
    };
    return axios.put(
        `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/setSellers/${values._id}`,
        jsonData,
        config
    );
};

const getCustomerBadge = (isNew) => {
    const text = isNew ? "Nuevo" : "Preexistente";
    return (
        <div className="salesPayment__badge__container">
            <Badge color={isNew ? "rgb(158, 192, 152)" : "rgb(150,61,61)"}>
                {text}
            </Badge>
        </div>
    );
};

const getStateBadge = (state, firstInstallmentState) => {
    let text, color;
    switch (state) {
        case "success":
            text = "Inscripto";
            color = "rgb(158, 192, 152)"; // var(--color-badge-green)
            break;
        case "cancelled":
            text = "Cancelado";
            color = "rgb(150,61,61)"; // var(--color-badge-red)
            break;
        default:
            text = "Pendiente";
            color = "rgb(197,189,132)"; // var(--color-badge-yellow)
    }
    if (state === "approval_pending" && firstInstallmentState === "PAID") {
        // This is a fix to handle not so good payment state.
        // If you want to change this. Relate to filtered rows on init function
        text = "Inscripto";
        color = "rgb(158, 192, 152)"; // var(--color-badge-green)
    }
    return (
        <div className="salesPayment__badge__container">
            <Badge color={color}>{text}</Badge>
        </div>
    );
};

const getAdminStateBadge = (state) => {
    let text, color;
    if (state === "success") {
        text = "Completas";
        color = "rgb(158, 192, 152)"; // var(--color-badge-green)
    } else {
        text = "Pendientes";
        color = "rgb(197,189,132)"; // var(--color-badge-yellow)
    }
    return (
        <div className="salesPayment__badge__container">
            <Badge color={color}>{text}</Badge>
        </div>
    );
};

function SalesPayments() {
    const { state: inscriptionUserData } = useLocation();
    const navigate = useNavigate()
    const { authToken } = useContext(AuthContext);
    const [salesPaymentsData, setSalesPaymentsData] = useState(null);
    const [displayForm, setDisplayForm] = useState(
        inscriptionUserData ? FORM_CONFIG.CREATE : FORM_CONFIG.NONE
    );
    const [initialValuesEdit, setInitialValuesEdit] = useState(
        inscriptionUserData ?? null
    );
    const [modalOpen, setModalOpen] = useState(false);
    const [studentData, setStudentData] = useState();
    const [loading, setLoading] = useState(false);
    const [fetchingLoading, setFetchingLoading] = useState(false);
    const [actionsLoading, setActionsLoading] = useState([]);
    const [updateData, setUpdateData] = useState(false);
    const [isInstallmentDetailsModal, setInstallmentDetailsModal] =
        useState(null);
    const { staffUsers, bankAccountsOptions } = useContext(AppContext);
    const firstDayOfThisMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
    );
    const currentDay = new Date();
    const [dateFilter, setDateFilter] = useState([
        firstDayOfThisMonth,
        currentDay,
    ]);

    const { uploadToS3 } = useS3();

    const PAYMENT_STATUS = {
        SUCCESS: "Success",
        CANCELLED: "Cancelled",
    };

    const handlePaymentConfirmation = async (paymentCode, paymentStatus) => {
        setActionsLoading((prev) => [...prev, paymentCode]);
        const data = {
            id: paymentCode,
            status: paymentStatus,
        };
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}payment/paymentRequest/`,
                { data },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );
            showNotification({
                color: "green",
                status: "success",
                title: "¡Operación exitosa!",
                message: "La inscripción del estudiante se ha confirmado.",
                autoClose: 5000,
            });
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo confirmar la inscripción. Detalle: ${ERROR_HANDLER[error.request?.status] ||
                    ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setUpdateData(!updateData);
            setActionsLoading((prev) =>
                prev.filter((item) => item !== paymentCode)
            );
        }
    };

    // const handleStudentData = (_studentData) => {
    //     setStudentData(_studentData);
    //     if (studentData !== undefined) {
    //         setModalOpen(true);
    //     }
    // };

    const handleStudentData = (_studentData) => {
        if (_studentData) {
            setStudentData(_studentData);
            setModalOpen(true);
        }
    };

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor("created_at", {
            cell: (info) => {
                const dateValue = new Date(info.getValue());
                return (
                    <div className="salesPayment-cell-center">
                        {moment(dateValue).format("DD/MM/YYYY")}
                    </div>
                );
            },
            header: "Fecha",
            size: 248,
            enableColumnFilter: false,
            enableSorting: true,
        }),
        columnHelper.accessor("dummyValue", {
            cell: (info) => {
                // Here you can configure the logic to indicate whether the admin has pending tasks or not.
                let badge = "pending";
                if (
                    (info.row.original.sellerId !== undefined &&
                        info.row.original.sellerId !== null) && (info.row.original.is_admin_check)
                ) badge = "success";
                return getAdminStateBadge(badge);
            },
            header: "Tareas ADMIN",
            size: 156,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "success", label: "Completas" },
                { value: "pending", label: "Pendientes" },
            ],
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;
                const hasSeller = row.original.sellerId !== undefined && row.original.sellerId !== null;
                const isAdminChecked = row.original.is_admin_check;

                const currentState = (hasSeller && isAdminChecked) ? "success" : "pending";
                return currentState === filterValue;
            },
            tooltip:
                "Indica si existen tareas pendientes. Ejemplo: Asignar el vendedor.",
        }),
        columnHelper.accessor("payer", {
            cell: (info) => {
                const value = info.getValue();
                return (
                    <span
                        className="salesPayment-studentData-span"
                        onClick={() => handleStudentData(value)}
                    >
                        {value.name} {value.lastname}
                    </span>
                );
            },
            header: "Datos del Alumno",
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.objectFilter,
        }),
        columnHelper.accessor("details", {
            cell: (info) => {
                const value = info.getValue();
                return value ? value : "No hay capacitación disponible";
            },
            header: "Capacitación",
            size: 304,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        columnHelper.accessor("state", {
            cell: (info) => {
                const firstInstallmentState =
                    info?.row?.original?.installments?.[0]?.state || "NULL";
                return getStateBadge(
                    info.getValue().toLowerCase(),
                    firstInstallmentState
                );
            },
            header: "Inscripción",
            size: 136,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "success", label: "Inscripto" },
                { value: "pending", label: "Pendiente" },
                { value: "cancelled", label: "Cancelado" },
            ],
            filterFn: filterFunctions.selectFilter,
            tooltip:
                "Indica si el alumno se encuentra inscripto en la capacitación y cuenta con acceso al campus.",
        }),
        columnHelper.accessor("payer.isNewCustomer", {
            cell: (info) => {
                const customer = info.getValue();
                return getCustomerBadge(customer);
            },
            header: "Nuevo",
            size: 88,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "true", label: "Nuevo" },
                { value: "false", label: "Preexistente" },
            ],
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;
                return (
                    row.original.payer.isNewCustomer.toString() ===
                    filterValue.toString()
                );
            },
            tooltip: "Indica si se trata de un nuevo estudiante para FORVET.",
        }),
        columnHelper.accessor("sellerId", {
            cell: (info) => {
                const staffUser = staffUsers.find(
                    (user) => user.value === info.getValue()
                );
                return staffUser
                    ? staffUser.label
                    : "No hay vendedor designado.";
            },
            header: "Vendedor",
            size: 120,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: staffUsers,
            filterFn: filterFunctions.selectFilter,
        }),
        columnHelper.accessor("payment.type", {
            cell: (info) => info.getValue(),
            header: "Plataforma",
            size: 164,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "PAYPAL", label: "PAYPAL" },
                { value: "PAGOS360", label: "PAGOS360" },
                { value: "TRANSFERENCIA", label: "TRANSFERENCIA" },
            ],
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;
                return (
                    row.original.payment.type.toLowerCase() ===
                    filterValue.toLowerCase()
                );
            },
        }),
        columnHelper.accessor("payment.bankTransferAccount", {
            cell: (info) => {
                const value = info.getValue()
                const bankAccount = bankAccountsOptions.find(account => account.value === value)
                if (bankAccount) {
                    return bankAccount.label
                }
                return value
            },
            header: "Cuenta",
            size: 164,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: bankAccountsOptions,
            filterFn: (row, id, filterValue) => {
                if (!filterValue || !row.original.payment.bankTransferAccount)
                    return true;
                return (
                    row.original.payment.bankTransferAccount.toLowerCase() ===
                    filterValue.toLowerCase()
                );
            },
        }),
        columnHelper.accessor("type", {
            cell: (info) => {
                const type = info.getValue();
                const installments = info?.row?.original?.installments;

                if (type === "SINGLE_PAYMENT") {
                    return "No";
                }

                if (type === "SUBSCRIPTION") {
                    if (
                        !Array.isArray(installments) ||
                        installments.length === 0
                    ) {
                        return "—";
                    }

                    // Tomamos cuántas cuotas deberían existir
                    const totalCycles = installments[0].total_cycles;

                    // Renderizamos rectángulos
                    return (
                        <div
                            onClick={() =>
                                setInstallmentDetailsModal(installments)
                            }
                            style={{
                                display: "flex",
                                gap: "4px",
                                overflowX: "auto",
                                width: "100%",
                                cursor: "pointer",
                            }}
                        >
                            {Array.from({ length: totalCycles }).map(
                                (_, index) => {
                                    // Buscamos si existe esa cuota con ese número (cycle)
                                    const installment = installments.find(
                                        (inst) => inst.cycle === index + 1
                                    );

                                    if (!installment) return;

                                    const isPaid =
                                        installment?.state === "PAID";
                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                minWidth: "20px",
                                                minHeight: "30px",
                                                borderRadius: "3px",
                                                backgroundColor: isPaid
                                                    ? "var(--color-green)"
                                                    : "var(--color-yellow)",
                                                color: isPaid ? "#fff" : "#000",
                                                fontSize: "10px",
                                                display: "grid",
                                                placeItems: "center",
                                            }}
                                        >
                                            {installment.cycle}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    );
                }

                return "";
            },
            header: "Cuotas",
            size: 120,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "SINGLE_PAYMENT", label: "No" },
                { value: "SUBSCRIPTION", label: "Si" },
            ],
            filterFn: filterFunctions.selectFilter,
        }),

        columnHelper.accessor("agreedAmount", {
            cell: (info) => `$${currencyFormat(info.getValue())}`,
            header: "Monto",
            size: 112,
            enableColumnFilter: false,
            enableSorting: true,
            tooltip:
                "Monto abonado por el estudiante. Después de aplicar el descuento.",
        }),
        columnHelper.accessor("payment.currency", {
            cell: (info) => info.getValue(),
            header: "Moneda",
            size: 88,
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "ARS", label: "ARS" },
                { value: "USD", label: "USD" },
            ],
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;
                return (
                    row.original.payment.currency.toLowerCase() ===
                    filterValue.toLowerCase()
                );
            },
        }),
        columnHelper.accessor("discount.percent", {
            cell: (info) => `${info.getValue()}%`,
            header: "DCTO",
            size: 88,
            enableColumnFilter: false,
            enableSorting: true,
        }),
        columnHelper.accessor("inscription_origin_code", {
            cell: (info) => info.getValue(),
            header: "COD ANUNCIO",
            size: 102,
            enableColumnFilter: true,

            filterFn: filterFunctions.textFilter,
            enableSorting: false,
        }),
        columnHelper.accessor("payer.is_already_in_wp", {
            cell: (info) => {
                const bool = info.getValue() === true ? "true" : "false";
                return <span>{bool === "true" ? "SI" : "NO"}</span>;
            },
            header: "Está en WP",
            size: 88,
            filter: "select",
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue(columnId); // true/false boolean
                const normalized = value ? "true" : "false";
                return normalized === filterValue;
            },
            filterOptions: [
                { value: "true", label: "SI" },
                { value: "false", label: "NO" },
            ],
            enableSorting: false,
            enableColumnFilter: true,
        }),
        columnHelper.accessor("commit", {
            cell: (info) => info.getValue(),
            header: "Comentarios",
            size: 400,
            filter: "text",
            enableSorting: false,
            enableColumnFilter: true,
            filterFn: filterFunctions.textFilter,
        }),
    ], [])

    // TABLE FUNCTIONS
    const editPaymentStateAction = (rowData) => {
        const paymentCode = rowData.gateway_id;
        const paymentDefaultStatus = rowData.state.toLowerCase();

        if (actionsLoading.includes(paymentCode)) return <Loader size="sm" />;
        return paymentDefaultStatus === "pending" ? (
            <>
                <IconSquareCheck
                    className="salesPayment-confirmation-icon"
                    onClick={() =>
                        handlePaymentConfirmation(
                            paymentCode,
                            PAYMENT_STATUS.SUCCESS
                        )
                    }
                />
                <IconSquareX
                    className="salesPayment-denied-icon"
                    onClick={() =>
                        handlePaymentConfirmation(
                            paymentCode,
                            PAYMENT_STATUS.CANCELLED
                        )
                    }
                />
            </>
        ) : null;
    };

    const editSalesPaymentsEvent = (rowData) => {
        if (rowData) {
            setInitialValuesEdit({
                rowData,
                academicUnitId: rowData?.academicUnitId,
                packId: rowData?.packId || null,
                subscriptionId: rowData?.subscriptionId,
                _id: rowData._id,
                gateway_id: rowData.gateway_id,
                inscriptionOrigin: rowData.inscriptionOrigin || "GENERAL",
                dollar_day: rowData.payment.dollar_day,
                isNewCustomer: rowData.payer.isNewCustomer ? "true" : "false",
                paymentMethod: rowData.payment?.type,
                inscription_origin_code: rowData?.inscription_origin_code || "",
                is_already_in_wp: rowData.payer.is_already_in_wp,
                wp_group_status: rowData?.payer?.wp_group_status || '',
                sellerId: rowData.sellerId,
                is_admin_check: rowData.is_admin_check,
                bankTransferAccount:
                    rowData.payment.bankTransferAccount ||
                    bankAccountsOptions[bankAccountsOptions.length - 1].value,
                type: rowData.type,
                commit: rowData.commit,
                agreedAmount: rowData.agreedAmount,
                installments: {
                    total_cycles: rowData.installments[0]?.total_cycles,
                    first_due_date:
                        moment(rowData.installments[0]?.date).toDate() || null,
                    data:
                        rowData.installments.length > 0
                            ? rowData.installments.map((_inst) => {
                                return {
                                    due_date: moment(_inst.date).toDate(),
                                    amount: _inst.agreedAmount,
                                    is_pre_deposit: _inst?.is_pre_deposit || false,
                                    state: _inst?.state || "PENDING",
                                    cycle_receipt: _inst?.cycle_receipt || null,
                                };
                            })
                            : null,
                },
                currency: rowData.payment.currency,
                trackingCode: rowData.tracking_code, // Tracking code to fetch the receipt
                receipt: rowData.receipt, // Initiates on null to force fetch on form
                discount: rowData.discount._id === "0" ? null : rowData.discount._id,
                discountId: rowData.discount._id === "0" ? null : rowData.discount._id,
                keepInstallmentsOnARS: rowData.payment.keepInstallmentsOnARS || false,
                hasAdvancedConfigEnabled: rowData.installments.length > 0 || false,
                amountDeposited: {
                    amount: rowData?.amountDeposited?.amount || 0,
                    currency: rowData?.amountDeposited?.currency || "USD",
                },
                student_type: rowData?.payer?.student_type || 'vet'
            });
            setDisplayForm(FORM_CONFIG.EDIT);
        }
    };
    const getFormAndPreview = (type, form) => {
        const isForm = type === "FORM";
        const isCreate =
            displayForm === FORM_CONFIG.CREATE || inscriptionUserData !== null;

        if (isForm && isCreate) {
            // Handle form creation
            return <NewSalesPaymentsForm form={form} loading={loading} />;
        } else if (isForm) {
            // Handle form editing
            return <SalesPaymentsForm form={form} loading={loading} />;
        } else if (isCreate) {
            // Handle preview creation
            return null;
        } else {
            // Handle preview editing
            return <SalesPaymentsView data={form.values} />;
        }
    };

    const handleSubmit = async (values) => {

        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + authToken,
            },
        };

        try {
            setLoading(true);


            let notificationMessage = "";

            if (values.hasOwnProperty("alreadyUser")) {

                // Venta de un pack: una sola venta con packId, el backend inscribe en cada curso
                if (values.sellPack) {
                    if (!values.packId) throw new Error("Elegí el pack a vender");
                    const { sellPack, ...packSaleValues } = values;
                    await createSale({ ...packSaleValues, academicUnitId: null }, config, uploadToS3);
                    notificationMessage = `La venta del pack para ${values.name || values.email} ha sido creada con éxito.`;
                } else {

                const academicUnitIds = values.academicUnitId
                    ? [].concat(values.academicUnitId)
                    : [];

                if (academicUnitIds.length > 0) {
                    // Creamos una promesa de createSale por cada academicUnitId
                    const salePromises = academicUnitIds.map((unitId) => {
                        const saleValues = {
                            ...values,
                            academicUnitId: unitId,
                        };

                        // Borramos la propiedad array del objeto que se enviará
                        delete saleValues.academicUnitId;
                        saleValues.academicUnitId = unitId; // Aseguramos que se envía como ID único

                        return createSale(saleValues, config, uploadToS3);
                    });

                    // Esperamos que todas las llamadas se completen
                    await Promise.all(salePromises);

                    // Mensaje para múltiples creaciones
                    notificationMessage = `Se han creado ${academicUnitIds.length} ventas para ${values.name} ${values.lastname} con éxito.`;

                } else {
                    await createSale(values, config, uploadToS3);
                    notificationMessage = `El pago de ${values.name} ${values.lastname} ha sido creado con éxito.`;
                }
                }



            } else {
                // Handle sale edit
                //TODO: review configuration
                await editSale(values, config, uploadToS3);
                notificationMessage = `El pago de ${values.rowData.payer.name} ${values.rowData.payer.lastname} ha sido editado con éxito.`;
            }


            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: notificationMessage,
            });

            setDisplayForm(FORM_CONFIG.NONE);
            setInitialValuesEdit(null);

            // Clean the URL state params to avoid form rendering issue
            navigate('/admin/ventas/pagos-ingresados', { replace: true });

            setUpdateData(!updateData);

        } catch (error) {
            console.error('❌ ERROR en handleSubmit:', error);
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / editar la inscripción. Detalle: ${ERROR_HANDLER[error.request?.status] ||
                    ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSetInitialValuesCreate = () => {
        setInitialValuesEdit({
            alreadyUser: false,
            type: "student",
            currency: "ARS",
            discount: null,
            academicUnitId: [],
            sellPack: false,
            packId: null,
            student_type: "vet",
            amountDeposited: {
                amount: 0,
                currency: "USD",
            }
        })
    }

    // DATA INITIALIZATION
    useEffect(() => {
        const init = async () => {
            try {
                setFetchingLoading(true);


                const formatDate = (date) => {
                    if (!date) return null;
                    const d = new Date(date);
                    if (isNaN(d.getTime())) return null;
                    return d.toISOString().split('T')[0];
                };

                const hasDateFilter = dateFilter && dateFilter.length > 0;

                const initDate = moment(hasDateFilter ? dateFilter[0] : firstDayOfThisMonth)
                    .startOf('day').toISOString();   // ej: 2026-07-01T03:00:00.000Z (00:00 ART)
                const endDate = moment(hasDateFilter ? dateFilter[1] : currentDay)
                    .endOf('day').toISOString();     // ej: 2026-07-02T02:59:59.999Z (23:59 ART)

                if (!initDate || !endDate) {
                    console.error('Fechas inválidas:', { initDate, endDate });
                    return;
                }

                const timestamp = Date.now();
                const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/paymentsTable?initDate=${initDate}&endDate=${endDate}&_t=${timestamp}`;

                const config = {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache',
                    },
                };

                const paymentsResponse = await axios.get(url, config);

                // Sort by date
                paymentsResponse.data.sort((a, b) => {
                    return new Date(b.created_at) - new Date(a.created_at);
                });

                // Filter non-confirmed payments
                const PAYMENTS_TO_FILTER = ["payer_action_required"];
                const filteredPayments = paymentsResponse.data.filter((_pay) => {
                    return !PAYMENTS_TO_FILTER.includes(_pay.state.toLowerCase());
                });

                // Also filter "APPROVAL_PENDING" payments that are not paid ("PENDING")
                const filteredPayments2 = filteredPayments.filter((_pay) => {
                    const firstInstallmentState = _pay.installments?.[0]?.state || "NULL";
                    return !(
                        _pay.state.toLowerCase() === "approval_pending" &&
                        firstInstallmentState === "PENDING"
                    );
                });

                setSalesPaymentsData(filteredPayments2);
            } catch (error) {
                console.error('❌ Error en useEffect:', error);
            } finally {
                setFetchingLoading(false);
            }
        };

        init();
    }, [dateFilter, updateData, authToken]);

    const memorizedData = useMemo(() => {
        if (!salesPaymentsData) return [];
        return salesPaymentsData.map((item) => ({
            ...item,
            highlighted: item?.division === "LARGE",
        }));
    }, [salesPaymentsData]);

    return (
        <div className="salesPayments__container">
            {displayForm === FORM_CONFIG.NONE && (
                <div className="AdministrativeExpensesView__filters">
                    <DatePickerInput
                        classNames={{
                            root: "moderationContainer-DatePicker-root",
                        }}
                        type="range"
                        label="Mostrando datos de:"
                        value={dateFilter}
                        onChange={(value) => setDateFilter(value)}
                    />
                    <span>
                        * Por defecto se muestran datos del mes actual (desde el
                        1ro hasta la fecha actual)
                    </span>
                </div>
            )}

            {displayForm !== FORM_CONFIG.NONE && (
                <ContentCreator
                    title={
                        initialValuesEdit
                            ? "Crear inscripción"
                            : "Editar inscripción"
                    }
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(FORM_CONFIG.NONE);
                        setInitialValuesEdit(null);
                    }}
                    initialValues={
                        inscriptionUserData || initialValuesEdit || null
                    }
                    renderPreview={(_form) =>
                        getFormAndPreview("PREVIEW", _form.form)
                    }
                    renderForm={(_form) =>
                        getFormAndPreview("FORM", _form.form)
                    }
                />
            )}
            {salesPaymentsData && (
                <TanStackTable
                    loading={loading || fetchingLoading}
                    data={memorizedData}
                    columns={columns}
                    displayForm={displayForm !== FORM_CONFIG.NONE}
                    editFunc={editSalesPaymentsEvent}
                    actionsFunc={(rowData) => editPaymentStateAction(rowData)}
                    addButtonFunc={() => {
                        setDisplayForm(FORM_CONFIG.CREATE);
                        handleSetInitialValuesCreate();
                        if (displayForm !== FORM_CONFIG.NONE)
                            setInitialValuesEdit(null);
                    }}
                />
            )}
            {modalOpen && studentData && (
                <StudentModal
                    open={modalOpen}
                    setOpen={setModalOpen}
                    studentData={studentData}
                />
            )}
            {isInstallmentDetailsModal && (
                <Modal
                    opened={isInstallmentDetailsModal}
                    onClose={() => setInstallmentDetailsModal(null)}
                >
                    <InstallmentsModal
                        isInstallmentDetailsModal={isInstallmentDetailsModal}
                        onClose={() => setInstallmentDetailsModal(null)}
                    />
                </Modal>
            )}
        </div>
    );
}

export default SalesPayments;

